/**
 * Durée d'immobilisation — presets partagés entre MaintenanceFormModal
 * (bouton "Créer et planifier") et MaintenanceDetailModal (panel Planifier).
 *
 * Convention : 1 "jour" = 1 jour ouvré (8h). Les durées < 1 signifient
 * que le véhicule est récupéré le jour même — pas de blocage du lendemain.
 * Backend (workflow_routes.mjs /schedule) calcule pickup_date en conséquence :
 *   duration < 1  → pickup = scheduled_date (same day)
 *   duration ≥ 1  → pickup = scheduled_date + ceil(duration - 1)
 *
 * i18n : ce module est hors React, il appelle donc `i18n.t()` directement.
 * ⚠️ Les libellés NE sont PAS calculés au chargement du module (ce qui figerait
 * la langue de démarrage) : chaque preset expose `label` en GETTER, résolu au
 * moment où le rendu le lit. La signature publique (tableau d'objets
 * `{ value, label }`, `formatDuration(days)`) est inchangée : les appelants de
 * `components/maintenance/*` n'ont rien à modifier.
 */
import i18n from '../i18n/index.js';

// Chaque preset : sa valeur en jours ouvrés + la clé i18n qui le nomme.
const PRESET_SPECS = [
  { value: 0.0625, key: 'common.duration.minutes',      opts: { count: 15 } },   // 0.5h
  { value: 0.125,  key: 'common.duration.minutes',      opts: { count: 30 } },   // 1h
  { value: 0.1875, key: 'common.duration.minutes',      opts: { count: 45 } },   // 1.5h
  { value: 0.25,   key: 'common.duration.hours',        opts: { count: 1, value: 1 } },
  { value: 0.375,  key: 'common.duration.hoursMinutes', opts: { hours: 1, minutes: 30 } },
  { value: 0.5,    key: 'common.duration.hours',        opts: { count: 2, value: 2 } },   // 4h
  { value: 0.75,   key: 'common.duration.hours',        opts: { count: 3, value: 3 } },   // 6h
  { value: 1,      key: 'common.duration.fullDay',      opts: {} },                       // 8h
  { value: 2,      key: 'common.duration.days',         opts: { count: 2 } },
  { value: 3,      key: 'common.duration.days',         opts: { count: 3 } },
  { value: 5,      key: 'common.duration.weeks',        opts: { count: 1 } },
];

export const DURATION_PRESETS = PRESET_SPECS.map(({ value, key, opts }) => ({
  value,
  get label() { return i18n.t(key, opts); },
}));

export const DEFAULT_DURATION = 0.25; // 1h — couvre 60%+ des interventions PME

export function formatDuration(days) {
  const d = Number(days);
  if (!isFinite(d) || d <= 0) return '—';
  if (d < 1) {
    const hours = d * 8;
    if (hours < 1) return i18n.t('common.duration.minutes', { count: Math.round(hours * 60) });
    return i18n.t('common.duration.hours', {
      count: hours,
      value: Number.isInteger(hours) ? hours : hours.toFixed(1),
    });
  }
  if (d === 1) return i18n.t('common.duration.oneDay');
  return i18n.t('common.duration.days', { count: d });
}

// ── Créneaux garage ────────────────────────────────────────────────
// Les garages prennent des RDV matin (~8h) jusqu'à 11h, puis après-midi
// jusqu'à ~15h (dernier dépôt raisonnable avant la fin de journée à 17h-18h).
// Ce n'est pas une règle dure — juste un guide pour l'UI.
export const GARAGE_OPEN_HOUR = 8;    // 08:00
export const GARAGE_CLOSE_HOUR = 18;  // 18:00 (fermeture typique)
export const GARAGE_LAST_DROPOFF = 15; // 15:00 (dernier dépôt raisonnable)

export function parseTimeHM(hm) {
  if (!hm || typeof hm !== 'string') return null;
  const m = hm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export function formatTimeHM(minutesSinceMidnight) {
  if (minutesSinceMidnight == null) return null;
  const total = ((minutesSinceMidnight % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// Preview heure de retour — miroir de la logique backend.
//   duration < 1 → same day, arrival + duration*8h
//   duration = 1 → same day, arrival + 8h
//   duration > 1 → arrival_time le jour de retour (retour matinal)
export function computePickupTime(arrivalHM, durationDays) {
  const arrival = parseTimeHM(arrivalHM);
  if (arrival == null) return null;
  const d = Number(durationDays);
  if (!isFinite(d) || d <= 0) return arrivalHM;
  if (d < 1) return formatTimeHM(arrival + Math.round(d * 8 * 60));
  if (d === 1) return formatTimeHM(arrival + 8 * 60);
  return arrivalHM;
}

// true si arrival + duration dépasse la fermeture garage (18h par défaut)
export function exceedsGarageClosing(arrivalHM, durationDays) {
  const arrival = parseTimeHM(arrivalHM);
  if (arrival == null) return false;
  const d = Number(durationDays);
  if (!isFinite(d) || d >= 1) return false; // Multi-day or full day: not applicable
  return arrival + d * 8 * 60 > GARAGE_CLOSE_HOUR * 60;
}
